import { useEffect, useState, type KeyboardEvent } from 'react';

import { Field, Modal, Notice } from '@/components/Modal';
import {
  usePasswordResetComplete,
  usePasswordResetConfirm,
  usePasswordResetStart,
  type PasswordResetChallenge,
} from '@/lib/auth/session';

type Step = 'identify' | 'code' | 'password';

/** 남은 초. 0 밑으로 내려가지 않는다. */
function useSecondsLeft(deadline: number | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (deadline === null) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [deadline]);
  return deadline === null ? 0 : Math.max(0, Math.ceil((deadline - now) / 1000));
}

function formatSeconds(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/**
 * 비밀번호 찾기 — 휴대폰 인증 세 걸음(V407).
 *
 * 이름·휴대폰 → 그 번호로 온 알림톡 인증번호 → 아이디를 보여 주고 새 비밀번호. 아이디는 받지 않는다
 * (사용자 지시 2026-10-07). 번호는 본인이 마이페이지에서 인증해 둔 것만 맞는다.
 */
export function PasswordResetModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const start = usePasswordResetStart();
  const confirm = usePasswordResetConfirm();
  const complete = usePasswordResetComplete();

  const [step, setStep] = useState<Step>('identify');
  const [phone, setPhone] = useState('');
  const [loginId, setLoginId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [challenge, setChallenge] = useState<PasswordResetChallenge | null>(null);
  const [deadline, setDeadline] = useState<number | null>(null);
  const [code, setCode] = useState('');
  const [ticket, setTicket] = useState('');
  const [nextPassword, setNextPassword] = useState('');
  const [againPassword, setAgainPassword] = useState('');
  const secondsLeft = useSecondsLeft(deadline);

  const pending = start.isPending || confirm.isPending || complete.isPending;
  const mismatch = againPassword !== '' && nextPassword !== againPassword;

  const canSend = Boolean(name.trim()) && phone.length === 11 && !pending;
  const canConfirm = Boolean(challenge && code.trim()) && secondsLeft > 0 && !pending;
  const canFinish = Boolean(ticket && nextPassword && nextPassword === againPassword) && !pending;

  const send = () => {
    if (!canSend) return;
    start.mutate(
      { name: name.trim(), phone: phone.trim() },
      {
        onSuccess: (sent) => {
          setChallenge(sent);
          setDeadline(Date.now() + sent.expiresInSeconds * 1000);
          setCode('');
          confirm.reset();
          setStep('code');
        },
      },
    );
  };

  const verify = () => {
    if (!canConfirm || !challenge) return;
    confirm.mutate(
      { verificationId: challenge.verificationId, code: code.trim() },
      {
        onSuccess: (result) => {
          setTicket(result.ticket);
          setLoginId(result.loginId);
          setStep('password');
        },
      },
    );
  };

  const finish = () => {
    if (!canFinish) return;
    complete.mutate({ ticket, newPassword: nextPassword }, { onSuccess: onDone });
  };

  const close = () => {
    if (pending) return;
    onClose();
  };

  const onEnter = (action: () => void) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.nativeEvent.isComposing) action();
  };

  const error = step === 'identify' ? start.error : step === 'code' ? (confirm.error ?? start.error) : complete.error;

  return (
    <Modal
      title="비밀번호 찾기"
      maxWidth="520px"
      onClose={close}
      closeDisabled={pending}
      footer={
        <>
          <button onClick={close} className="btn-secondary" disabled={pending}>
            취소
          </button>
          {step === 'identify' && (
            <button onClick={send} className="btn-primary" disabled={!canSend}>
              {start.isPending ? '보내는 중...' : '인증번호 받기'}
            </button>
          )}
          {step === 'code' && (
            <>
              <button onClick={send} className="btn-secondary" disabled={!canSend}>
                {start.isPending ? '보내는 중...' : '다시 받기'}
              </button>
              <button onClick={verify} className="btn-primary" disabled={!canConfirm}>
                {confirm.isPending ? '확인 중...' : '확인'}
              </button>
            </>
          )}
          {step === 'password' && (
            <button onClick={finish} className="btn-primary" disabled={!canFinish}>
              {complete.isPending ? '변경 중...' : '변경'}
            </button>
          )}
        </>
      }
    >
      {step === 'identify' && (
        <>
          <Field label="이름">
            <input
              className="input-field"
              autoComplete="name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={onEnter(send)}
              disabled={pending}
            />
          </Field>
          <Field label="휴대폰 번호">
            <input
              className="input-field"
              inputMode="numeric"
              autoComplete="tel"
              maxLength={11}
              value={phone}
              onChange={(event) => setPhone(event.target.value.replace(/\D/g, '').slice(0, 11))}
              onKeyDown={onEnter(send)}
              disabled={pending}
            />
          </Field>
        </>
      )}

      {step === 'code' && challenge && (
        <Field label={`인증번호 · ${challenge.maskedPhone} · ${formatSeconds(secondsLeft)}`}>
          <input
            className="input-field"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
            onKeyDown={onEnter(verify)}
            disabled={pending}
            autoFocus
          />
        </Field>
      )}

      {step === 'password' && (
        <>
          {loginId && (
            <Field label="아이디">
              <input className="input-field" value={loginId} readOnly />
            </Field>
          )}
          <Field label="새 비밀번호">
            <input
              className="input-field"
              type="password"
              autoComplete="new-password"
              value={nextPassword}
              onChange={(event) => setNextPassword(event.target.value)}
              onKeyDown={onEnter(finish)}
              disabled={pending}
              autoFocus
            />
          </Field>
          <Field label="새 비밀번호 확인">
            <input
              className="input-field"
              type="password"
              autoComplete="new-password"
              value={againPassword}
              onChange={(event) => setAgainPassword(event.target.value)}
              onKeyDown={onEnter(finish)}
              disabled={pending}
            />
          </Field>
          {mismatch && <Notice tone="error">두 비밀번호가 다릅니다.</Notice>}
        </>
      )}

      {error && <Notice tone="error">{error.message}</Notice>}
    </Modal>
  );
}
